import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_GROUPED_QUERY_KEY } from '@/app/(main)/client-detail/hooks/useGetClientGroupedQuery';
import { CLIENT_CREATE_QUERY_KEY, RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import { additionalPhones } from '@/app/(main)/client-detail/utils/contacts';
import type { ClientInfoType, CreateClientFormData, EditClientData } from '@/types/clientsApiTypes';
import { emailValidation, nullableString, validatePhoneNumber } from '@/utils/validations';
import { EditRelatedContactInfoForm } from '../../EditSectionModals/components/EditRelatedContactInfoForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getRelatedAddressPayload } from '../../EditSectionModals/utils/getAddressPayload';
import { getRelatedBasicInfoPayload } from '../../EditSectionModals/utils/getRelatedBasicInfoPayload';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    client: yup.object().shape({
      email: yup
        .string()
        .email('Invalid email')
        .test('is-valid-email', 'Invalid email', (value) => emailValidation(value ?? ''))
        .optional()
        .defined(),
      additional_emails: yup
        .array()
        .of(
          yup.object({
            email: yup
              .string()
              .email('Invalid email')
              .test('is-valid-email', 'Invalid email', (value) => emailValidation(value ?? ''))
              .required('Email is required'),
          })
        )
        .required()
        .default([]),
      phones: yup
        .array()
        .of(
          yup.object({
            phone: yup
              .string()
              .optional()
              .defined()
              .test(
                'valid-phone-format',
                'Invalid phone number',
                (value) => !value || value.trim() === '' || validatePhoneNumber(value)
              ),
            title: yup.string().nullable().defined(),
          })
        )
        .required()
        .default([]),
      phone: nullableString()
        .optional()
        .test('valid-phone-format', 'Invalid phone number', (value) => {
          if ((value?.length ?? 0) <= 3) return true;
          return validatePhoneNumber(value);
        }),
    }),
  }),
  related_passengers: yup.array().of(
    yup.object().shape({
      client: yup.object().shape({
        same_as_primary: yup.boolean().optional().defined().default(false),
        client_mailing_address: nullableString().optional().defined(),
        client_mailing_address_line_2: nullableString().optional(),
        client_mailing_city: nullableString().optional().defined(),
        client_mailing_state: nullableString().optional().defined(),
        client_mailing_country: nullableString().optional().defined(),
        zip_code: nullableString()
          .optional()
          .defined()
          .max(20, 'The client zip code field must not be greater than 20 characters.'),
      }),
    })
  ),
});

function useRelatedContactInfoOnSave() {
  const mutation = useEditClientMutation();
  const queryClient = useQueryClient();

  return async (payload: Partial<EditClientData>) => {
    await mutation.mutateAsync(
      {
        data: payload,
        clientId: Number(payload.client?.id),
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
        },
      }
    );
  };
}

const Fields = () => <EditRelatedContactInfoForm />;

export const relatedContactInfoSection = defineSection<
  (typeof ModalKeys)['related_contact_info'],
  CreateClientFormData,
  typeof schema,
  Partial<EditClientData>
>({
  key: ModalKeys.related_contact_info,
  title: 'Contact Information',
  subtitle: 'Passenger contact details and mailing address.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: {
      client: {
        ...c.primary_passenger.client,
        // Override with traveler's contact info (traveler is related_passengers[0])
        email: c.related_passengers[0]?.client.email ?? c.primary_passenger.client.email,
        phone: c.related_passengers[0]?.client.phone ?? null,
        phones: additionalPhones(c.related_passengers[0]?.client.phones),
        additional_emails: c.related_passengers[0]?.client.additional_emails ?? [],
      },
    },
    related_passengers: c.related_passengers.map((passenger) => ({
      client: {
        ...passenger.client,
        same_as_primary: passenger.client.same_as_primary ?? false,
        client_mailing_address: passenger.client.client_mailing_address ?? '',
        client_mailing_address_line_2: passenger.client.client_mailing_address_line_2 ?? '',
        client_mailing_city: passenger.client.client_mailing_city ?? '',
        client_mailing_state: passenger.client.client_mailing_state ?? '',
        client_mailing_country: passenger.client.client_mailing_country ?? '',
        zip_code: passenger.client.zip_code ?? '',
      },
    })),
  }),
  mapValuesToPayload: (v, client): Partial<EditClientData> => {
    const travelerId = client?.related_passengers?.[0]?.client.client_id ?? 0;
    const primaryClient = v.primary_passenger.client as ClientInfoType;
    const relatedClient = v.related_passengers?.[0]?.client;

    // Merge: contact fields from primary_passenger (used as traveler in modal),
    // address fields from related_passengers[0]
    const contactPayload = getRelatedBasicInfoPayload({
      client_id: travelerId,
      email: primaryClient.email ?? null,
      additional_emails: primaryClient.additional_emails ?? [],
      phones: additionalPhones(primaryClient.phones),
      phone: primaryClient.phone ?? null,
      crm_emails: client?.related_passengers?.[0]?.client.crm_emails,
      crm_phones: client?.related_passengers?.[0]?.client.crm_phones,
      preferred_name: primaryClient.preferred_name ?? null,
      relationship: primaryClient.relationship ?? null,
      first_name: primaryClient.first_name,
      middle_name: primaryClient.middle_name ?? null,
      last_name: primaryClient.last_name,
      gender: primaryClient.gender ?? null,
      date_of_birth: primaryClient.date_of_birth ?? null,
      preferred_airport_ids: primaryClient.preferred_airport_ids ?? [],
      userHasCertifiedTheInformation: primaryClient.userHasCertifiedTheInformation ?? false,
    });

    const addressPayload = relatedClient
      ? getRelatedAddressPayload({
          ...v.primary_passenger.client,
          client_id: travelerId,
          client_mailing_address: relatedClient.client_mailing_address ?? '',
          client_mailing_address_line_2: relatedClient.client_mailing_address_line_2 ?? '',
          client_mailing_city: relatedClient.client_mailing_city ?? '',
          client_mailing_state: relatedClient.client_mailing_state ?? '',
          client_mailing_country: relatedClient.client_mailing_country ?? '',
          zip_code: relatedClient.zip_code ?? '',
        })
      : {};

    return {
      client: {
        ...contactPayload,
        ...addressPayload,
        id: travelerId,
      },
    };
  },
  useOnSave: useRelatedContactInfoOnSave,
  Fields,
});
